import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getAlbum, getAlbumCover, getArtist, getNextAlbum, getPreviousAlbum, getTracks, postQueue, postTrack } from "../../components/apis/music-api";
import { Album as AlbumType } from '../../types/Album';
import { Artist as ArtistType } from '../../types/Artist';
import { FileResponse } from "../../types/FileResponse";
import { Track } from "../../types/Track";
import { Box, Button, Modal, Stack, TextField, Typography, useMediaQuery } from "@mui/material";
import { useMusicStore } from "../../components/store/store";
import { LastFmPlaylistItem } from "../../types/LastFmPlaylist";
import { format, splitAlbumName, trackDuration, zeroPad } from "../../components/text-helper/text-helper";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import ButtonBar from "../../components/button-bar/ButtonBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookReader, faCancel, faCheck, faEdit, faList, faPenToSquare, faPlay, faPlus, faSave, faSortAmountAsc } from "@fortawesome/free-solid-svg-icons";
import AlbumLink from "../../components/album-link/AlbumLink";
import { postTrackRequest } from "../../types/messages/postTrackRequest";
import callApi from "../../components/call-api/call-api";
import PlaylistModal from "../../modals/playlist-modal/playlist-modal";
import { modalStyle } from "../../modals/modal-style";
import { usePlaylistModal } from "../../hooks/usePlaylistModal";
import LyricsModal from "../../modals/lyrics-modal/lyrics-modal";
import { PlayModes } from "../../types/PlayModes";
import CoverDisplay from "../../components/cover-display/CoverDisplay";
import { Recording } from "../../types/Recording";
import 'react-h5-audio-player/lib/styles.css';
import { useLyricsModal } from "../../hooks/useLyricsModal";

type AlbumProps = {
  letter: string,
  artistName: string,
  albumName: string,
}

const Album = () => {
  const defaultAlbum: AlbumType = { letter: '', artistName: '', year: 0, title: '', thumbnail: new Uint8Array(0), trackCount: 0, totalDuration: 0  };
  
  const params = useParams<AlbumProps>();
  const { getMusicToken, getAccessToken } = useContext(AuthenticationContext);
  const { applyItemToPlaylists } = usePlaylistModal();
  const lyricsModal = useLyricsModal();
  const [ artist, setArtist ] = useState<ArtistType>();
  const [ album, setAlbum ] = useState<AlbumType>(defaultAlbum);
  const [ previousAlbum, setPreviousAlbum ] = useState<AlbumType>(defaultAlbum);
  const [ nextAlbum, setNextAlbum ] = useState<AlbumType>(defaultAlbum);
  const [ cover, setCover ] = useState<FileResponse>();
  const [ tracks, setTracks ] = useState<Track[]>();
  const [ open, setOpen ] = useState(false);
  const [ queuePopup, setQueuePopup ] = useState(false);
  const [ trackToEdit, setTrackToEdit ] = useState<Track>();
  const [ trackToEditIndex, setTrackToEditIndex ] = useState(0);
  const [ totalTime, setTotalTime ] = useState(0);
  const { currentTrack, setPlaylist, setPlayMode } = useMusicStore();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const apiUrl = process.env.REACT_APP_API_URL;
  
  useEffect(() => {
    callApi<AlbumType>(getAlbum(getAccessToken(), params.artistName ?? '', params.albumName ?? ''), setAlbum);
    callApi<AlbumType>(getPreviousAlbum(getAccessToken(), params.artistName ?? '', params.albumName ?? ''), setPreviousAlbum);
    callApi<AlbumType>(getNextAlbum(getAccessToken(), params.artistName ?? '', params.albumName ?? ''), setNextAlbum);
  }, [params, getAccessToken]);

  useEffect(() => {
    if (album !== undefined && album.title !== '' && album.year > 0) {
      callApi<ArtistType>(getArtist(getAccessToken(), params.artistName ?? ''), setArtist);
      callApi<FileResponse>(getAlbumCover(getAccessToken(), params.artistName ?? '', params.albumName ?? ''), setCover);
      callApi<Track[]>(getTracks(getAccessToken(), params.artistName ?? '', params.albumName ?? ''), setTracks);
    }
  }, [album, params.artistName, params.albumName, getAccessToken]);

  useEffect(() => {
    if (tracks !== undefined) {
      const sum = tracks.reduce((accumulator: number, track) => accumulator + (track.duration ?? 0), 0);
      setTotalTime(sum);
    }
  }, [tracks]);

  const handleTrackAddToQueueClicked = (index?: number) => {
    if (tracks !== undefined) {
      const selectedTracks = index === undefined ? tracks : [tracks[index]];
      const recordings = selectedTracks.map((track) => {
        return {
          letter: params.letter,
          artistName: params.artistName,
          albumYear: splitAlbumName(params.albumName ?? '').year as unknown as number,
          albumTitle: splitAlbumName(params.albumName ?? '').title,
          track,
        } as Recording
      });

      callApi<boolean>(postQueue(getAccessToken(), recordings), () => {});
      setQueuePopup(true);
    }
  }

  const handleAlbumAddToQueueClicked = () => {
    if (tracks !== undefined) {
      const recordings = tracks.map((track) => {
        return {
          letter: params.letter,
          artistName: params.artistName,
          albumYear: splitAlbumName(params.albumName ?? '').year as unknown as number,
          albumTitle: splitAlbumName(params.albumName ?? '').title,
          track,
        } as Recording
      });

      callApi<boolean>(postQueue(getAccessToken(), recordings), () => {});
      setQueuePopup(true);
    }
  }

  const handleLyricsClicked = (index?: number) => {
    if (tracks !== undefined && index !== undefined) {
      const track = tracks[index];
      lyricsModal.setArtist(params.artistName);
      lyricsModal.setTrack(track.name);
      lyricsModal.setLyricsModalOpen(true);
    }
  }

  const handlePlayClicked = (index?: number) => {
    if (tracks !== undefined) {
      const musicToken = getMusicToken();
      const selectedTracks = index === undefined ? tracks : [tracks[index]];
      const trackList = selectedTracks.map((t) => {
        const trackName = `${zeroPad(t.number, 2)} - ${t.name}`;
        return {
          letter: params.letter,
          artist: params.artistName,
          album: params.albumName,
          trackNumber: t.number,
          name: t.name,
          scrobbleArtist: format(params.artistName ?? ''),
          src: `${apiUrl}/Stream/Artists/${params.artistName ?? ''}/Albums/${(params.albumName ?? '')}/Tracks/${trackName}?accessToken=${musicToken}`
        } as LastFmPlaylistItem
      });

      setPlayMode(trackList.length > 1 ? PlayModes.Album : PlayModes.Track);
      setPlaylist(trackList);
    }
  }

  const handleEditTrackClicked = (index?: number) => {    
    if (tracks !== undefined && index !== undefined) {
      setTrackToEditIndex(index);
      setTrackToEdit(tracks[index]);
      setOpen(true);
    }
  }

  const handleTrackUpdate = () => {
    if (tracks !== undefined && trackToEdit?.name !== undefined && trackToEdit.name.length > 0) {      
      const updateTrack = async () => {
        const trackName = `${String(tracks[trackToEditIndex].number).padStart(2, '0')} - ${tracks[trackToEditIndex].name}`;
        const trackRequest: postTrackRequest = {
          number: trackToEdit?.number,
          name: trackToEdit.name,
          duration: trackToEdit.duration ?? 0,
        }

        const isUpdated = await postTrack(getAccessToken(), params.artistName ?? '', params.albumName ?? '', trackName, trackRequest);
        if (isUpdated !== undefined) {
          callApi<Track[]>(getTracks(getAccessToken(), params.artistName ?? '', params.albumName ?? ''), setTracks);
        }

        setOpen(false);
      }

      updateTrack();
    }
  }

  const isPlaying = (index: number) => {
    return currentTrack?.artist === params.artistName
      && currentTrack?.album === params.albumName
      && currentTrack?.trackNumber === index;
  }
  
  const getStyle = (key: string, defaultValue: string = 'inherit') => {
    const style = artist?.styles?.filter(x => x.key === key);
    return (style === undefined || style.length === 0)
      ? defaultValue
      : style[0].value;
  }

  return (
    <>
      <PlaylistModal />
      <LyricsModal />
      <Modal open={queuePopup} onClose={() => setQueuePopup(false)}>
        <Stack spacing={1} direction="column" sx={modalStyle}>
          <Box>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Added to the queue
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Button variant="contained" size="small" onClick={() => setQueuePopup(false)} color="error">
              <FontAwesomeIcon fontSize="small" color="primary" icon={faCheck} className="icon" />
              Okay
            </Button>
          </Box>
        </Stack>
      </Modal>
      <Modal open={open}>
        <Stack spacing={1} direction="column" sx={modalStyle}>
          <Box>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Edit track number {String(trackToEdit?.number).padStart(2, '0')}
            </Typography>
          </Box>
          <Box>
            <TextField 
              id="track-name"
              sx={{ width: '320px' }}
              size="small"
              label="Name"
              InputLabelProps={{ shrink: true }}
              variant="outlined" 
              value={trackToEdit?.name}
              onChange={(e) => {
                if (trackToEdit?.number) {
                  setTrackToEdit({
                    ...trackToEdit,
                    name: e.target.value
                  });
                }
              }}
            />
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Button variant="contained" size="small" onClick={() => setOpen(false)} color="error">
              <FontAwesomeIcon fontSize="small" color="primary" icon={faCancel} className="icon" />
              Cancel
            </Button>
            <Button variant="contained" size="small" onClick={handleTrackUpdate} color="primary">
              <FontAwesomeIcon fontSize="small" color="primary" icon={faSave} className="icon" />
              Save
            </Button>
          </Box>
        </Stack>
      </Modal>
      <ButtonBar
        leftButtons={[
          <Button variant="contained" size="small" onClick={() => handlePlayClicked()}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faPlay} className="icon" />
            Play
          </Button>,
          <Button variant="contained" size="small" onClick={() => handleAlbumAddToQueueClicked()}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faSortAmountAsc} className="icon" />
            Add To Queue
          </Button>,
        ]}
        rightButtons={[
          <Button variant="contained" size="small" onClick={() => applyItemToPlaylists(artist, album, undefined)}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faList} className="icon" />
            Add to Playlist
          </Button>,
          <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${(params.artistName ?? '')}/albums/${(params.albumName ?? '')}/edit`}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faPenToSquare} className="icon" />
            Edit
          </Button>
        ]}
      />
      <Stack spacing={3}>
        <Box
          sx={{
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: getStyle('background-color'),
            color: getStyle('color'),
          }}
        >
          <Stack direction={isMedium ? 'column' : 'row'}>
            <Box sx={{ width: '100%', marginRight: '12px', paddingBottom: '24px' }}>
              <Stack direction={isMedium ? 'column' : 'row'} sx={{ paddingBottom: '12px' }}>
                <Box>
                  <Typography variant="h5">{album?.title}</Typography>
                  <Typography variant="h6">
                    by <Link to={`/letters/${params.letter}/artists/${(params.artistName ?? '')}`} style={{ color: getStyle('color') }}>
                      {format(params.artistName ?? '')}
                    </Link>
                  </Typography>
                </Box>
                <Box alignContent="right" sx={{ marginLeft: 'auto', textAlign: 'right' }}> 
                  <Typography variant="h5">{album?.year}</Typography>
                  <Typography variant='body1' color={getStyle('color', 'grey')}>({trackDuration(totalTime)})</Typography>
                </Box>
              </Stack>
              <Box>
                <Stack spacing={1}>
                  {tracks?.map(track =>
                    <Stack
                      direction={isSmall ? 'column' : 'row'}
                      sx={{
                        ...(isSmall && { borderBottom: '1px solid #888' }),
                        paddingBottom: '8px'
                      }}
                      spacing={2}
                    >
                      <Box>
                        <Stack direction="row" spacing={0.5}>
                          <Box>
                            <Button
                              variant="contained"
                              size="small"
                              style={{ minWidth: 0 }}
                              onClick={() => handleEditTrackClicked(track.number - 1)}
                            >
                              <FontAwesomeIcon fontSize="medium" color="primary" icon={faEdit} className="icon" style={{ marginRight: 0 }} />
                            </Button>
                          </Box>
                          <Box>
                            <Button
                              variant="contained"
                              size="small"
                              style={{ minWidth: 0 }}
                              onClick={() => applyItemToPlaylists(artist, album, tracks[track.number - 1])}
                            >
                              <FontAwesomeIcon fontSize="medium" color="primary" icon={faPlus} className="icon" style={{ marginRight: 0 }} />
                            </Button>
                          </Box>
                          <Box>
                            <Button
                              variant="contained"
                              size="small"
                              style={{ minWidth: 0 }}
                              onClick={() => handleTrackAddToQueueClicked(track.number - 1)}
                            >
                              <FontAwesomeIcon fontSize="medium" color="primary" icon={faSortAmountAsc} className="icon" style={{ marginRight: 0 }} />
                            </Button>
                          </Box>
                          <Box>
                            <Button
                              variant="contained"
                              size="small"
                              style={{ minWidth: 0 }}
                              onClick={() => handleLyricsClicked(track.number - 1)}
                            >
                              <FontAwesomeIcon fontSize="medium" color="primary" icon={faBookReader} className="icon" style={{ marginRight: 0 }} />
                            </Button>
                          </Box>
                          <Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={isPlaying(track.number)}
                              style={{ minWidth: 0 }}
                              onClick={() => handlePlayClicked(track.number - 1)}
                            >
                              <FontAwesomeIcon fontSize="medium" color="primary" icon={faPlay} className="icon" style={{ marginRight: 0 }} />
                            </Button>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        {String(track.number).padStart(2, '0')}
                      </Box>
                      <Box>
                        <Stack direction="column">
                          <Box pr={2}>
                            {track.name} ({trackDuration(track.duration)})
                          </Box>
                          <Box sx={{ fontSize: '0.8rem', color: getStyle('color', 'grey') }}>
                            listens: {track.listenCount}
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
            <Box
              alignContent={isSmall ? 'center' : 'right'} 
              sx={{ textAlign: 'center' }}
            >
              <CoverDisplay cover={cover} />
              <Box sx={{ width: isMedium ? '100%' : '500px' }}>
                <Box sx={{ float: 'left' }}>
                  <AlbumLink
                    letter={params.letter ?? ''}
                    artistName={params.artistName ?? ''}
                    album={previousAlbum}
                    onClick={() => setCover(undefined)}
                  />
                </Box>
                <Box sx={{ float: 'right'  }}>
                  <AlbumLink
                    letter={params.letter ?? ''}
                    artistName={params.artistName ?? ''}
                    album={nextAlbum}
                    onClick={() => setCover(undefined)}
                  />
                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </>
  )
}

export default Album;